import type { Degree, Distance, Point } from "@pdfking/math"
import { Cos, Scale, Sin, Sqrt, Subtract, ToRadians, Total } from "@pdfking/math"
import { MM } from "@pdfking/quantity"

import type { FolderCardCorners, FolderCorner } from "#internal"
import type { FolderPath } from "#public"

import { FolderLines } from "./FolderLines"

// A business card's half.
const HALF_CARD = [MM(42.5), MM(27.5)] as const
// Each slit: how far in from the card's corner its middle is, and half its length.
const INSET = Scale(MM(7), 1 / Sqrt(2))
const HALF = MM(10)
// How far each slit's middle is from the card's centre, across and down.
const SPREAD = [Subtract(HALF_CARD[0], INSET), Subtract(HALF_CARD[1], INSET)] as const

const SIGNS: Record<FolderCardCorners, [number, number][]> = {
  tr_bl: [
    [1, -1],
    [-1, 1],
  ],
  tl_br: [
    [-1, -1],
    [1, 1],
  ],
  top: [
    [-1, -1],
    [1, -1],
  ],
}

/** The slits holding a business card by two `corners`, centred on `centre`:
 * across each corner at `angle` from the card's long side (45° unless the
 * template cuts them steeper), their middles `spread` from `centre` (closer
 * than a card's corners where the template holds it tighter), the card's own
 * size at any size of the folder, and none unless they all fit in `region`. */
export const FolderCardSlits = (
  region: FolderCorner[],
  centre: Point<Distance>,
  corners: FolderCardCorners,
  angle: Degree = 45 as Degree,
  spread = SPREAD
): FolderPath[] => {
  const [dx, dy] = [Scale(HALF, Cos(ToRadians(angle))), Scale(HALF, Sin(ToRadians(angle)))]
  return FolderLines(
    region,
    ...SIGNS[corners].map(([sx, sy]): [Point<Distance>, Point<Distance>] => {
      const x = Total(centre.x, Scale(spread[0], sx))
      const y = Total(centre.y, Scale(spread[1], sy))
      return [
        { x: Total(x, Scale(dx, -sx)), y: Total(y, Scale(dy, sy)) },
        { x: Total(x, Scale(dx, sx)), y: Total(y, Scale(dy, -sy)) },
      ]
    })
  )
}
